/**
 * /wireline-slickline/reports/:id — one end-of-job service report
 * (GET /wireline-slickline/reports/:id and its actions, fieldiq-api#1339, WL-3).
 *
 * LIFECYCLE (the API's): draft → issued → void, one live report per job.
 *
 * - A DRAFT has no number. Its content is the copy the API last built from the
 *   job's office-approved day sheets; `stale` says the rows have moved since,
 *   and Refresh rebuilds it (it may also change the title, summary and
 *   comments, which is how the text is edited — there is no PATCH).
 * - ISSUE numbers it WLR-NNNN, freezes it and stores the PDF. The API refuses
 *   it unless the job is completed and has an approved day sheet
 *   (409 job_not_completed / no_approved_sheets); the refusal is shown as the
 *   API's sentence. The button is not pre-disabled on a guess about the job.
 * - AN ISSUED REPORT IS IMMUTABLE: no Refresh, no Issue, no text edit. Only the
 *   PDF and (office) Void remain. A 409 report_issued that still arrives is shown.
 * - VOID needs a reason and is WL_ADMIN (admin, office_staff); the button is
 *   hidden from other roles, and the API is the authority either way.
 *
 * WARNINGS NEVER BLOCK: sheets not yet approved, a misfire count that
 * disagrees, a source with incomplete custody times, and (on issue) content
 * that changed since the last refresh are shown beside the report.
 *
 * Every figure in the content is the API's (ReportContent.tsx).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Activity, Ban, ClipboardList, Download, FileText, Pencil, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useIssueWlReport, useRefreshWlReport, useVoidWlReport, useWlReport } from '../../hooks/useWlReports';
import { claimBlobTab } from '../../lib/blob-tab';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtWlNumber } from '../../lib/wireline-slickline';
import {
  canVoidWlReports,
  canWriteWlReports,
  openWlReportPdf,
  wlReportNumber,
  wlReportRefusal,
  wlReportStatusSkin,
  wlReportWarningText,
} from '../../lib/wireline-slickline-reports';
import type { WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { useBackToList } from '../downhole/recordPage';
import { WlReportContentView } from './ReportContent';
import {
  isNotFound,
  WL_BAND_SECONDARY,
  WL_EYEBROW,
  WL_INPUT,
  WL_PRIMARY,
  WL_SECONDARY,
  WlBadge,
  WlDialog,
  WlEmpty,
  WlField,
  WlFormError,
  WlLoading,
  WlPanel,
  WlSpec,
  WlWarning,
} from './WlBits';

export const wlReportPath = (id: string) => `/wireline-slickline/reports/${id}`;

const sortedJson = (v: unknown): string =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? `{${Object.keys(v as Record<string, unknown>).sort().map((k) => `${JSON.stringify(k)}:${sortedJson((v as Record<string, unknown>)[k])}`).join(',')}}`
    : Array.isArray(v)
      ? `[${v.map(sortedJson).join(',')}]`
      : JSON.stringify(v);
const sameWarning = (a: unknown, b: unknown) => sortedJson(a) === sortedJson(b);

export default function WlReportDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/wireline-slickline/reports');
  const { user } = useAuth();
  const query = useWlReport(id);
  const report = query.data?.data ?? null;
  const tz = query.data?.meta?.timeZone ?? null;
  const canWrite = canWriteWlReports(user?.role);
  const canVoid = canVoidWlReports(user?.role);
  const draft = report?.status === 'draft';
  const refresh = useRefreshWlReport();
  const issue = useIssueWlReport();
  const voidIt = useVoidWlReport();
  const busy = refresh.isPending || issue.isPending || voidIt.isPending;

  const [actionError, setActionError] = useState<string | null>(null);
  const [writeWarnings, setWriteWarnings] = useState<WlWarningEntry[]>([]);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState({ title: '', summary: '', comments: '' });
  const [issuing, setIssuing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setActionError(null);
    setWriteWarnings([]);
  }, [id]);

  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));

  const doRefresh = (body?: { title: string | null; summary: string | null; comments: string | null }) => {
    setActionError(null);
    setWriteWarnings([]);
    refresh.mutate(
      { id: report!.id, body },
      {
        onSuccess: () => {
          toast.success(body ? 'Report saved and refreshed' : 'Report refreshed');
          setEditing(false);
        },
        onError: (err) => setActionError(wlReportRefusal(err, 'The report could not be refreshed.')),
      },
    );
  };

  const openPdf = async () => {
    setPdfError(null);
    const win = claimBlobTab(); // before the await, or the browser blocks the tab
    const err = await openWlReportPdf(report!.id, win);
    if (err) setPdfError(err);
  };

  const contentWarnings = report?.status === 'void' ? [] : report?.content?.warnings ?? [];
  // A write's warnings that the stored content does not already carry (content_changed_since_refresh on issue).
  // Compared key-sorted: the write's warnings are built in JS, the content's come back out of jsonb, which
  // reorders keys, so a plain JSON.stringify would call two equal warnings different and show them twice.
  const extraWarnings = writeWarnings.filter((w) => !contentWarnings.some((c) => c.code === w.code && sameWarning(c, w)));

  return (
    <div data-testid="wl-report-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={report ? (report.reportNumber ?? `Draft report · ${report.jobNumber ?? 'Job'}`) : query.isLoading ? 'Service report' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          report ? (
            <div className="flex flex-wrap gap-2">
              {draft && canWrite && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  disabled={busy}
                  onClick={() => { setActionError(null); setIssuing(true); }}
                  data-testid="wl-report-issue"
                >
                  <Send className="size-4" aria-hidden="true" />
                  Issue
                </button>
              )}
              {draft && canWrite && (
                <button type="button" className={WL_BAND_SECONDARY} disabled={busy} onClick={() => doRefresh()} data-testid="wl-report-refresh">
                  <RefreshCw className="size-4" aria-hidden="true" />
                  Refresh
                </button>
              )}
              {draft && canWrite && !editing && (
                <button
                  type="button"
                  className={WL_BAND_SECONDARY}
                  onClick={() => {
                    setText({ title: report.title ?? '', summary: report.summary ?? '', comments: report.comments ?? '' });
                    setActionError(null);
                    setEditing(true);
                  }}
                  data-testid="wl-report-edit"
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit text
                </button>
              )}
              <button type="button" className={WL_BAND_SECONDARY} onClick={openPdf} data-testid="wl-report-pdf">
                <Download className="size-4" aria-hidden="true" />
                Download PDF
              </button>
              {report.status !== 'void' && canVoid && (
                <button
                  type="button"
                  className={WL_BAND_SECONDARY}
                  disabled={busy}
                  onClick={() => { setVoidReason(''); setVoidError(null); setVoiding(true); }}
                  data-testid="wl-report-void"
                >
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: report ? wlReportStatusSkin(report.status).label : '—' },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Job', tone: 'neutral', loading: query.isLoading, value: report?.jobNumber || '—' },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Runs',
            tone: 'accent',
            loading: query.isLoading,
            value: report?.content ? fmtWlNumber(report.content.totals?.runCount) : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading report" />
          ) : notFound || (!failure && !report) ? (
            <WlPanel eyebrow="Service report" title="Service report">
              <WlEmpty>This report was not found. It may belong to a job you are not on.</WlEmpty>
            </WlPanel>
          ) : failure || !report ? (
            <WlPanel eyebrow="Service report" title="Service report">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this report" />
            </WlPanel>
          ) : (
            <>
              <WlFormError message={actionError} />
              <WlFormError message={pdfError} />

              {report.status === 'void' && (
                <p data-testid="wl-report-void-banner" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
                  This report is void{report.voidedAt ? ` (${formatInstant(report.voidedAt, tz) ?? '—'})` : ''}: {report.voidReason || '—'}. The job can have a new report.
                </p>
              )}

              {draft && report.stale && (
                <div
                  data-testid="wl-report-stale"
                  className="flex flex-wrap items-center justify-between gap-3 rounded-fi-control border border-fi-signal-amber/40 bg-fi-signal-amber/10 px-3 py-2 text-sm text-fieldiq-text"
                >
                  <span>The work recorded on this job has changed since this draft was built. Refresh it to bring the report up to date.</span>
                  {canWrite && (
                    <button type="button" className={WL_SECONDARY} disabled={busy} onClick={() => doRefresh()} data-testid="wl-report-stale-refresh">
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Refresh
                    </button>
                  )}
                </div>
              )}

              {[...extraWarnings, ...contentWarnings].map((w, i) => (
                <WlWarning key={`${w.code}-${i}`} message={wlReportWarningText(w)} testId={`wl-report-warning-${w.code}`} />
              ))}

              <WlPanel eyebrow="Service report" title={report.title || 'End-of-job service report'} testId="wl-report-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Number" value={wlReportNumber(report)} mono />
                  <WlSpec label="Status" value={<WlBadge skin={wlReportStatusSkin(report.status)} value={report.status} />} />
                  <WlSpec
                    label="Job"
                    value={
                      <Link className="text-fi-amber underline-offset-2 hover:underline" to={`/wireline-slickline/jobs/${report.jobId}`}>
                        {report.jobNumber || '—'}
                      </Link>
                    }
                    mono
                  />
                  <WlSpec label="Customer" value={report.customerName || '—'} />
                  <WlSpec label="Well" value={report.wellName || '—'} />
                  <WlSpec label="Content built" value={formatInstant(report.contentBuiltAt, tz) ?? '—'} />
                  <WlSpec label="Issued" value={formatInstant(report.issuedAt, tz) ?? '—'} />
                  <WlSpec label="Signed by" value={report.issuedSignatoryName || '—'} />
                </dl>
                {(report.summary || report.comments) && (
                  <div className="space-y-2 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    {report.summary && <p className="whitespace-pre-line">{report.summary}</p>}
                    {report.comments && <p className="whitespace-pre-line text-fieldiq-muted">{report.comments}</p>}
                  </div>
                )}
              </WlPanel>

              {editing && draft && canWrite && (
                <WlPanel eyebrow="Edit" title="Report text" testId="wl-report-edit-form">
                  <form
                    className="space-y-3 px-4 py-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const t = (v: string) => (v.trim() === '' ? null : v.trim());
                      doRefresh({ title: t(text.title), summary: t(text.summary), comments: t(text.comments) });
                    }}
                  >
                    <WlField label="Title" hint="Optional. The PDF reads End-of-Job Service Report when empty.">
                      <input className={WL_INPUT} maxLength={200} aria-label="Report title" value={text.title} onChange={(e) => setText({ ...text, title: e.target.value })} />
                    </WlField>
                    <WlField label="Summary">
                      <textarea className={`${WL_INPUT} h-28 py-2`} maxLength={4000} aria-label="Report summary" value={text.summary} onChange={(e) => setText({ ...text, summary: e.target.value })} />
                    </WlField>
                    <WlField label="Comments">
                      <textarea className={`${WL_INPUT} h-28 py-2`} maxLength={8000} aria-label="Report comments" value={text.comments} onChange={(e) => setText({ ...text, comments: e.target.value })} />
                    </WlField>
                    <p className="text-xs text-fieldiq-muted">Saving also refreshes the report from the work recorded now.</p>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button type="button" className={WL_SECONDARY} onClick={() => setEditing(false)} disabled={refresh.isPending}>
                        Cancel
                      </button>
                      <button type="submit" className={WL_PRIMARY} disabled={refresh.isPending}>
                        {refresh.isPending ? 'Saving…' : 'Save text'}
                      </button>
                    </div>
                  </form>
                </WlPanel>
              )}

              {report.content ? (
                <WlReportContentView content={report.content} />
              ) : (
                <WlPanel eyebrow="Content" title="Content">
                  <WlEmpty>The report has no stored content.</WlEmpty>
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>

      {report && (
        <WlDialog
          open={issuing}
          title={`Issue the report for ${report.jobNumber ?? 'this job'}`}
          onClose={() => setIssuing(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setIssuing(false)} disabled={issue.isPending}>
                Not yet
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={issue.isPending}
                data-testid="wl-report-issue-confirm"
                onClick={() => {
                  setActionError(null);
                  setWriteWarnings([]);
                  issue.mutate(report.id, {
                    onSuccess: (res) => {
                      toast.success(res.data?.reportNumber ? `Report ${res.data.reportNumber} issued` : 'Report issued');
                      setWriteWarnings(res.warnings);
                      setIssuing(false);
                    },
                    onError: (err) => {
                      setActionError(wlReportRefusal(err, 'The report could not be issued.'));
                      setIssuing(false);
                    },
                  });
                }}
              >
                {issue.isPending ? 'Issuing…' : 'Issue report'}
              </button>
            </>
          }
        >
          <p className="text-sm text-fieldiq-text">
            Issuing rebuilds the report from the work recorded now, gives it a WLR- number, signs it with your name and stores the PDF. An issued
            report cannot be changed; it can only be voided by the office.
          </p>
        </WlDialog>
      )}

      {report && (
        <WlDialog
          open={voiding}
          title={`Void ${report.reportNumber ?? 'this draft'}`}
          onClose={() => setVoiding(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setVoiding(false)} disabled={voidIt.isPending}>
                Keep report
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={voidIt.isPending || voidReason.trim() === ''}
                data-testid="wl-report-void-confirm"
                onClick={() =>
                  voidIt.mutate(
                    { id: report.id, reason: voidReason.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Report voided');
                        setVoiding(false);
                      },
                      onError: (err) => setVoidError(wlReportRefusal(err, 'The report could not be voided.')),
                    },
                  )
                }
              >
                {voidIt.isPending ? 'Voiding…' : 'Void report'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <WlFormError message={voidError} />
            <WlField label="Reason" hint="Required. Kept on the report and printed on its PDF.">
              <textarea className={`${WL_INPUT} h-24 py-2`} aria-label="Void reason" maxLength={1000} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
            </WlField>
            <p className="text-sm text-fieldiq-text">A void is final. The job can then have a new report.</p>
          </div>
        </WlDialog>
      )}
    </div>
  );
}
